import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../firebase";
import { e2eJacanaMock } from "../utils/e2eJacanaMock";
import type { BookingLegalContext, ConsentSelection } from "../features/legal/legalConfig";
import type { BookingSelection } from "../features/jacana/pricing/pricingEngine";

const functions = getFunctions(app, "europe-west1");

export interface InitiateBookingParams {
  outingId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  participantsCount: number;
  pricingSelection?: BookingSelection[];
  specialNeeds?: string;
  paymentMethod: 'mollie' | 'bank_transfer' | 'on_site';
  returnUrl: string;
  legalContext?: BookingLegalContext;
  legalAcceptance?: ConsentSelection;
}

export interface InitiateBookingResponse {
  success: boolean;
  bookingId: string;
  checkoutUrl?: string;
  paymentMethod: string;
  bankTransferCommunication?: string;
  bankTransferIban?: string;
}

export interface GetPublicBookingStatusParams {
  bookingId: string;
  outingId?: string;
  paymentId?: string;
}

export interface GetPublicBookingStatusResponse {
  exists: boolean;
  bookingStatus: string;
  paymentStatus: string;
  isPaid: boolean;
  outingId: string;
  participantsCount: number;
  amount: number;
  updatedAt: string | null;
  statusLabel: "verifying" | "confirmed" | "failed";
}

type OrganizationBankAccountSource = {
  paymentSettings?: {
    bankAccount?: string | null;
  };
} | null | undefined;

type OutingBankAccountSource = {
  bankAccountOverride?: string | null;
} | null | undefined;

type CallableErrorShape = {
  code?: string;
  message?: string;
  details?: {
    message?: string;
  } | unknown;
};

function isCallableErrorShape(value: unknown): value is CallableErrorShape {
  return typeof value === 'object' && value !== null;
}

export function getEffectiveBankAccount({
  organization,
  outing,
}: {
  organization: OrganizationBankAccountSource;
  outing: OutingBankAccountSource;
}): string | null {
  return (
    outing?.bankAccountOverride ||
    organization?.paymentSettings?.bankAccount ||
    null
  );
}

export function normalizeCallableError(error: unknown, fallbackMessage = "Une erreur est survenue.") {
  const normalizedError = new Error(fallbackMessage) as Error & {
    code?: string;
    details?: unknown;
  };

  if (isCallableErrorShape(error)) {
    normalizedError.message = error.details && typeof error.details === 'object' && error.details !== null && 'message' in error.details
      ? String((error.details as { message?: string }).message || error.message || fallbackMessage)
      : String(error.message || fallbackMessage);
    normalizedError.code = error.code;
    normalizedError.details = error.details;
    return normalizedError;
  }

  return normalizedError;
}

export async function callBackendFunction<TRequest, TResponse>(name: string, params: TRequest): Promise<TResponse> {
  if (e2eJacanaMock.isEnabled()) {
    const mockedResult = e2eJacanaMock.runAction(name, params);
    if (mockedResult !== null) {
      return mockedResult as TResponse;
    }
  }

  try {
    const callable = httpsCallable<TRequest, TResponse>(functions, name);
    const result = await callable(params);
    return result.data;
  } catch (error) {
    console.error(`Erreur lors de l'appel backend ${name}:`, error);
    throw normalizeCallableError(error, `Échec de l'action ${name}.`);
  }
}

export const paymentService = {
  async initiateBooking(params: InitiateBookingParams): Promise<InitiateBookingResponse> {
    return callBackendFunction<InitiateBookingParams, InitiateBookingResponse>("initiateBooking", params);
  },

  async getPublicBookingStatus(params: GetPublicBookingStatusParams): Promise<GetPublicBookingStatusResponse> {
    return callBackendFunction<GetPublicBookingStatusParams, GetPublicBookingStatusResponse>("getPublicBookingStatus", params);
  },
};
